import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LineChart } from "@mui/x-charts/LineChart";

function getRangeMs(r) {
  const map = { "1H": 1, "3H": 3, "6H": 6, "12H": 12, "1D": 24, "3D": 72, "1W": 168 };
  return (map[r] || 24) * 60 * 60 * 1000;
}

function breakGaps(data, maxGapMs = 60 * 60 * 1000) {
  if (!data.length) return [];
  const result = [data[0]];
  for (let i = 1; i < data.length; i++) {
    const gap = data[i].time - data[i - 1].time;
    if (gap > maxGapMs) {
      result.push({ time: data[i - 1].time + 1, I: null });
      result.push({ time: data[i].time - 1, I: null });
    }
    result.push(data[i]);
  }
  return result;
}

export default function VoltampermeCurrentsPage() {
  const { t, i18n } = useTranslation();
  const { deviceId } = useParams();
  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  const [chartData, setChartData] = useState([]);
  const [minMax, setMinMax] = useState({ Imin: null, Imax: null });
  const [range, setRange] = useState("1D");

  const locale = i18n.language === "tr" ? "tr-TR" : "en-US";

  useEffect(() => {
    async function fetchChart() {
      try {
        const end = new Date();
        const start = new Date(end.getTime() - getRangeMs(range));
        const url = `${API_BASE}/voltampermetre/currents?deviceId=${deviceId}&start=${start.toISOString()}&end=${end.toISOString()}`;
        const res = await fetch(url);
        const rows = await res.json();
        const formatted = breakGaps(
          rows.map(r => ({ time: new Date(r.time).getTime(), I: r.I }))
        );
        setChartData(formatted);
      } catch (err) {
        console.error("currents chart error:", err);
      }
    }
    fetchChart();
  }, [deviceId, range, API_BASE]);

  useEffect(() => {
    async function fetchMinMax() {
      try {
        const res = await fetch(`${API_BASE}/voltampermetre/realtime?deviceId=${deviceId}`);
        const d = await res.json();
        setMinMax({ Imin: d.Imin, Imax: d.Imax });
      } catch (err) {
        console.error("realtime minmax error:", err);
      }
    }
    fetchMinMax();
  }, [deviceId, API_BASE]);

  const now = new Date();
  const xMin = new Date(now.getTime() - getRangeMs(range));
  const ranges = ["1H", "3H", "6H", "12H", "1D", "3D", "1W"];

  return (
    <div className="p-6 space-y-6 bg-slate-100 min-h-screen">
      <h2 className="text-2xl font-semibold text-slate-800">{t("voltampermetre.current")}</h2>

      {/* Min / Max cards */}
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-white rounded-xl shadow-sm p-4 text-center">
          <p className="text-xs text-gray-500 mb-1">{t("voltampermetre.minCurrent")}</p>
          <p className="text-2xl font-bold text-gray-600">
            {minMax.Imin != null ? Number(minMax.Imin).toFixed(2) : "—"} <span className="text-sm font-normal">A</span>
          </p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-4 text-center">
          <p className="text-xs text-gray-500 mb-1">{t("voltampermetre.maxCurrent")}</p>
          <p className="text-2xl font-bold text-red-500">
            {minMax.Imax != null ? Number(minMax.Imax).toFixed(2) : "—"} <span className="text-sm font-normal">A</span>
          </p>
        </div>
      </div>

      {/* Chart */}
      <div className="bg-white rounded-xl shadow-sm p-5">
        {/* Range buttons */}
        <div className="flex flex-wrap gap-2 mb-4">
          {ranges.map(r => (
            <button
              key={r}
              onClick={() => setRange(r)}
              className={`px-3 py-1 rounded-md text-sm font-medium border transition ${
                range === r
                  ? "bg-green-600 text-white border-green-600"
                  : "bg-white text-gray-600 border-gray-300 hover:border-gray-400"
              }`}
            >
              {t(`timeRanges.${r}`)}
            </button>
          ))}
        </div>

        {chartData.length > 0 ? (
          <LineChart
            height={320}
            dataset={chartData}
            xAxis={[{
              dataKey: "time",
              scaleType: "time",
              min: xMin,
              max: now,
              valueFormatter: (v) => {
                const d = new Date(v);
                return ["1H", "3H", "6H", "12H", "1D"].includes(range)
                  ? d.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" })
                  : d.toLocaleDateString(locale, { day: "2-digit", month: "short" });
              },
            }]}
            series={[{
              dataKey: "I",
              label: "I (A)",
              color: "#22c55e",
              showMark: ["1H", "3H"].includes(range),
              connectNulls: false,
            }]}
            margin={{ left: 60, right: 20, top: 20, bottom: 40 }}
          />
        ) : (
          <div className="h-64 flex items-center justify-center text-slate-400">
            {t("common.loading")}
          </div>
        )}
      </div>
    </div>
  );
}
